import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axiosServices from 'utils/axios';

// ==============================|| TYPE DEFINITIONS ||============================== //

interface Integration {
  id: string;
  name: string;
  description: string;
  status: 'connected' | 'disconnected' | 'coming-soon';
  connectionCount: number;
  icon: string;
  color: string;
  managementUrl: string | null;
  isAvailable: boolean;
}

interface IntegrationsState {
  integrations: Integration[];
  status: 'idle' | 'loading' | 'success' | 'failed';
  error: string;
}

const initialState: IntegrationsState = {
  integrations: [],
  status: 'idle',
  error: '',
};

// ==============================|| ASYNC THUNKS ||============================== //

export const fetchIntegrations = createAsyncThunk(
  'integrations/fetchIntegrations',
  async (organizationId: string, { rejectWithValue }) => {
    try {
      // Use axiosServices which has 401 interceptor for auto token refresh
      const response = await axiosServices.get(`/integrations?organizationId=${organizationId}`);
      return response.data;
    } catch (error: any) {
      // If the error is from the axios interceptor (refresh failed + redirect to login),
      // the error won't have a response. In this case, don't reject - just return empty.
      if (!error.response) {
        return null;
      }
      return rejectWithValue(error.response?.data?.message || error.message || 'Failed to fetch integrations');
    }
  }
);

// ==============================|| ASYNC ACTION HANDLERS ||============================== //

const asyncActionHandlers = {
  [fetchIntegrations.pending.type]: { status: 'loading' as const },
  [fetchIntegrations.fulfilled.type]: (state: IntegrationsState, action: any) => {
    if (!action.payload) {
      state.integrations = [];
      state.status = 'success';
      state.error = '';
      return;
    }
    // Backend wraps response in { response: { integrations: [...] }, metadata: {...} }
    state.integrations = action.payload.response?.integrations || [];
    state.status = 'success';
    state.error = '';
  },
  [fetchIntegrations.rejected.type]: (state: IntegrationsState, action: any) => {
    state.status = 'failed';
    state.error = action.payload || 'Failed to fetch integrations';
  },
};

// ==============================|| SLICE ||============================== //

export const integrationsSlice = createSlice({
  name: 'integrations',
  initialState,
  reducers: {
    clearError(state) {
      state.error = '';
    },
    clearIntegrationsState(state) {
      state.integrations = [];
      state.status = 'idle';
      state.error = '';
    },
  },
  extraReducers: (builder) => {
    Object.entries(asyncActionHandlers).forEach(([type, handler]) => {
      builder.addCase(type, (state, action) => {
        if (typeof handler === 'function') {
          handler(state, action);
        } else {
          const options = handler;
          state.status = options.status;
        }
      });
    });
  },
});

// ==============================|| ACTIONS ||============================== //

export const { clearError, clearIntegrationsState } = integrationsSlice.actions;

// ==============================|| SELECTORS ||============================== //

export const selectIntegrations = (state: any) => state.integrations.integrations;
export const selectIntegrationsStatus = (state: any) => state.integrations.status;
export const selectIntegrationsError = (state: any) => state.integrations.error;

// ==============================|| REDUCER ||============================== //

export default integrationsSlice.reducer;
